import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { createFolder } from '@/services/api/endpoints';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import { useNotify } from '@/features/notifications/useNotify';
import type { Folder } from '@/types/domain';

interface CreateFolderArgs {
  containerId: string;
  parentId: string | null;
  name: string;
}

export function useCreateFolder() {
  const qc = useQueryClient();
  const { notifyError, notifyWarning } = useNotify();

  return useMutation<Folder, Error, CreateFolderArgs>({
    mutationFn: async (args) => {
      addBreadcrumb({
        category: 'mutation',
        message: 'create-folder',
        data: { containerId: args.containerId, parentId: args.parentId },
      });
      return createFolder(args);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folders'] });
      // Un dossier créé depuis les résultats de recherche doit pouvoir y
      // apparaître (il porte souvent le terme cherché — c'est même la raison
      // d'avoir cherché). Cf. `useFolderMutations.invalidateTree`.
      qc.invalidateQueries({ queryKey: ['search'] });
    },
    // The failure MUST be announced here rather than at each call site: the three
    // entry points (folder-detail grid, tree ContainerSection, tree FolderRow)
    // each passed only `onSettled`, so a refusal closed the dialog and changed
    // nothing on screen — the user saw a no-op with no explanation.
    onError: (err, args) => {
      if (err instanceof HTTPError && err.response.status === 409) {
        // uq_folders_sibling: unique per (container, parent, lower(name)) — so the
        // clash is case-INSENSITIVE, and "fluides" collides with "FLUIDES". Say
        // that, otherwise a user who sees a differently-cased name in the list
        // thinks the app is broken.
        notifyWarning(
          'Dossier déjà existant',
          `Un dossier « ${args.name} » existe déjà à cet endroit (la casse n’est pas prise en compte). Choisissez un autre nom.`,
        );
        return;
      }
      notifyError('Création du dossier impossible', err.message);
    },
  });
}
